import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationsList from 'src/shared/Notifications/NotificationsList';
import { Paths } from 'src/pages/paths';
import { Link, useLocation } from 'react-router-dom';
import Tooltip from 'src/common/Tooltip';
import Badge from 'src/common/Badge';
import Skeleton from 'src/common/Skeleton';
import styles from './index.module.scss';
import { trackClickOnNotificationsList, trackClickOnShowAllNotifications } from 'src/segment/events';
import Dropdown from 'src/common/Dropdown';
import HeaderAction from '../HeaderAction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import Button from 'src/common/Button';
import {
  useMarkNotificationsAsRead,
  useMarkNotificationsAsSeen,
  useLoadNotifications,
  useNewNotificationSubscriptions
} from 'src/models/notifications/hooks';

export const NotificationsMenu: FC = () => {
  const { t } = useTranslation();

  const [menuVisible, setMenuVisible] = useState(false);
  const [markAllNotificationsAsSeen] = useMarkNotificationsAsSeen();
  const { data, loading } = useLoadNotifications();
  useNewNotificationSubscriptions();

  const toggleMenu = useCallback(() => {
    setMenuVisible(vis => {
      if (!vis) {
        !!data?.unseenCount && markAllNotificationsAsSeen();
        trackClickOnNotificationsList();
      }
      return !vis;
    });
  }, [data, markAllNotificationsAsSeen]);

  const onClose = useCallback(() => {
    setMenuVisible(false);
  }, []);

  const [markAllNotificationsAsRead] = useMarkNotificationsAsRead();

  const isUnreadNotifications = useMemo(
    () => !!data && !!data.notifications.filter(notification => !notification.isRead).length,
    [data]
  );
  const markAllAsRead = useCallback(() => {
    markAllNotificationsAsRead();
  }, [markAllNotificationsAsRead]);

  const location = useLocation();
  const { pathname, search } = location;

  const backHref = useMemo(() => {
    return `${pathname}${search}`;
  }, [pathname, search]);

  return data && !data.notifications.length ? (
    <Tooltip trigger={'click'} title={t('Header.emptyNotifications')}>
      <HeaderAction>
        <FontAwesomeIcon icon={faBell} className={styles.icon} />
      </HeaderAction>
    </Tooltip>
  ) : (
    <Dropdown
      trigger={['click']}
      getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
      visible={menuVisible}
      onVisibleChange={toggleMenu}
      overlayClassName={styles.notificationsMenu}
      overlay={
        <div className={styles.menu}>
          <div className={styles.header}>
            <h4>{t('Notifications.notifications')}</h4>
            {isUnreadNotifications && (
              <Button type={'link'} onClick={markAllAsRead}>
                {t('Header.markAllAsRead')}
              </Button>
            )}
          </div>
          <div className={styles.notificationsList}>
            {loading ? (
              <Skeleton loading active className={styles.loader} />
            ) : (
              <NotificationsList showMarkDown={false} limit={10} onClose={onClose} />
            )}
          </div>
          <div className={styles.footer}>
            {/* FIXME: should we remove this link and corresponding page? */}
            <Link
              onClick={() => {
                trackClickOnShowAllNotifications();
              }}
              to={{ pathname: Paths.NOTIFICATIONS_PAGE }}
              state={{ from: backHref }}
              className={styles.link}
            >
              {t('Common.showAll')}
            </Link>
          </div>
        </div>
      }
    >
      <HeaderAction>
        <Badge count={data && (menuVisible ? 0 : data.unseenCount)} overflowCount={9} className={styles.iconBadge}>
          <FontAwesomeIcon data-action="toggle" data-name="notification-button" icon={faBell} className={styles.icon} />
        </Badge>
      </HeaderAction>
    </Dropdown>
  );
};

export default NotificationsMenu;
